import { useState, useRef, useEffect, useCallback, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
// import { Link } from "react-router-dom"; // [HIDDEN] Only used by legacy invitation link
import { useAuthStore } from "@/stores/auth-store";
import { authApi } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { generateCodeVerifier, generateCodeChallenge, generateState } from "@/lib/pkce";
import { takeReturnTo } from "@/lib/auth/return-to";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/use-page-title";
import { Label } from "@/components/ui/label";
import { isLocalLoginEnabled } from "@/lib/config";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { ToolboxSignIn } from "@/components/toolbox/toolbox-sign-in";
import { motion, useInView } from "framer-motion";
import {
  Lightbulb24Regular,
  TaskListSquareLtr24Regular,
  Rocket24Regular,
  CheckmarkStarburst24Regular,
  CheckmarkCircle16Filled,
  Circle16Regular,
  ShieldCheckmark20Filled,
  Play20Filled,
  Eye24Regular,
  PlugConnected20Regular,
  DocumentCopy20Regular,
  ArrowRepeatAll20Regular,
  Notepad20Regular,
  BookOpen20Regular,
  PeopleTeam20Regular,
  TextDescription20Regular,
  BranchFork20Regular,
} from "@fluentui/react-icons";

/* ------------------------------------------------------------------ */
/*  Scroll-triggered fade-up wrapper                                  */
/* ------------------------------------------------------------------ */
function FadeUp({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Animated counter                                                  */
/* ------------------------------------------------------------------ */
function Counter({
  target,
  suffix = "",
  containerRef,
}: {
  target: number;
  suffix?: string;
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  const inView = useInView(containerRef, { once: true, margin: "-40px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      setCount(Math.round(t * target));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(frame); };
  }, [inView, target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Terminal-style mockup (used for Monitor section)                   */
/* ------------------------------------------------------------------ */
function MockTerminal({
  children,
  title,
}: {
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#0c0c0d] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
        </div>
        {title && (
          <span className="text-[11px] text-white/25 ml-1 font-mono">
            {title}
          </span>
        )}
      </div>
      <div className="p-4 font-mono text-[11.5px] leading-[1.8] text-white/50 overflow-x-auto">
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Data                                                              */
/* ------------------------------------------------------------------ */
const steps = [
  {
    Icon: Lightbulb24Regular,
    title: "Describe",
    desc: "Tell AI what you want to build in plain language",
  },
  {
    Icon: TaskListSquareLtr24Regular,
    title: "Plan",
    desc: "AI breaks it into epics, features, and tasks",
  },
  {
    Icon: Rocket24Regular,
    title: "Execute",
    desc: "Multiple agents build in parallel with full context",
  },
  {
    Icon: CheckmarkStarburst24Regular,
    title: "Ship",
    desc: "Automated validation proves everything works",
  },
];

const capabilities = [
  {
    Icon: PlugConnected20Regular,
    title: "95+ MCP Tools",
    desc: "Full API surface for AI agents to manage work items, sessions, and context",
  },
  {
    Icon: DocumentCopy20Regular,
    title: "Template Engine",
    desc: "Turn successful implementations into repeatable workflows with one command",
  },
  {
    Icon: ArrowRepeatAll20Regular,
    title: "Session Handoff",
    desc: "Context, decisions, and progress preserved automatically across sessions",
  },
  {
    Icon: Notepad20Regular,
    title: "Decision Logging",
    desc: "Every choice recorded with rationale, alternatives, and impact analysis",
  },
  {
    Icon: BookOpen20Regular,
    title: "Skill Packs",
    desc: "Shareable knowledge packages that encode domain expertise for agents",
  },
  {
    Icon: PeopleTeam20Regular,
    title: "Team Workspaces",
    desc: "Personal and team scopes with role-based access and isolation",
  },
  {
    Icon: TextDescription20Regular,
    title: "Structured Descriptions",
    desc: "AI instructions, acceptance criteria, and file references on every item",
  },
  {
    Icon: BranchFork20Regular,
    title: "Execution Plans",
    desc: "Dependency-aware phase planning with parallel groups and critical paths",
  },
];

const integrations = [
  {
    name: "PowerShell",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M23.181 2.974c.568 0 .923.463.792 1.035l-3.659 15.982c-.13.572-.697 1.035-1.265 1.035H.819c-.568 0-.923-.463-.792-1.035L3.686 3.009c.13-.572.697-1.035 1.265-1.035h18.23zM5.31 18.178l.135-.59h13.11l.135-.59H5.58l.135-.59h.59l3.076-5.382L6.44 8.67l-.135.59 2.202 2.463-2.59 4.533H5.31l-.135.59h.135v1.332zm5.69-4.533l.59-.197-2.2-2.463 2.59-4.533h.607l.135-.59H7.03l-.135.59h.607L4.913 11.19l2.886 3.274-.135.59h.607l.135-.59-.59.197.59-.197-.59.197z"/>
      </svg>
    ),
  },
  {
    name: "Visual Studio",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M2.15 18.306L0 16.78V7.217l2.15-1.525 5.053 4.058L14.607.378 18 1.81v20.382l-3.393 1.43-7.404-7.273-5.053 3.957zm14.986.885V4.81L10.95 12l6.186 7.191zm-14.078-2.09l3.684-5.1-3.684-5.1v10.2z"/>
      </svg>
    ),
  },
  {
    name: "VS Code",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
      </svg>
    ),
  },
  {
    name: "Terminal",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M0 3a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V3zm2 1v16h20V4H2zm4.146 3.146a.5.5 0 0 1 .708 0l3.5 3.5a.5.5 0 0 1 0 .708l-3.5 3.5a.5.5 0 0 1-.708-.708L9.293 11 6.146 7.854a.5.5 0 0 1 0-.708zM11.5 14.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5z"/>
      </svg>
    ),
  },
];

/**
 * Maps a manual-login failure to the message shown on the sign-in page.
 *
 * `/auth/login` is exempt from the client's refresh-and-retry branch, so a 401
 * here unambiguously means bad credentials. Every other failure is a problem
 * with the request itself and is reported as such, since this form is a
 * local-development affordance and a misleading error costs debugging time.
 */
function describeLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    const status = err.response.status;
    if (status === 401) return "Invalid email or password";
    // ApiError degrades to `API Error: <status>` when the response body isn't
    // JSON or carries no error.message. Interpolating that after the status
    // prefix reads as "Sign-in failed (502): API Error: 502", so fall back to
    // the HTTP reason phrase instead.
    const detail =
      err.message === `API Error: ${String(status)}`
        ? err.response.statusText || "unexpected error"
        : err.message;
    return `Sign-in failed (${String(status)}): ${detail}`;
  }
  // fetch() rejects with a TypeError when the API is unreachable — the common
  // case when the dev server is running but the API is not.
  return `Sign-in failed: ${err instanceof Error ? err.message : "unknown error"}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export function LoginPage() {
  usePageTitle("Login");
  const navigate = useNavigate();
  // Inside SpecTree Toolbox the preload sets the bridge on the SpecTree origin, so /login sees it.
  const toolboxBridge = useToolboxBridge();
  const { login } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [showSignInFloat, setShowSignInFloat] = useState(false);
  const [mobileGlowY, setMobileGlowY] = useState(50);
  const loginSectionRef = useRef<HTMLDivElement>(null);
  const landingSectionRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const doLogin = async () => {
      try {
        await login(email, password);
        void navigate(takeReturnTo());
      } catch (err) {
        // Only a 401 from /auth/login means the credentials were wrong. Anything
        // else (API down, 5xx, proxy misconfigured) must surface as itself —
        // this form exists for local debugging, so masking those as a
        // credentials failure sends developers down the wrong path.
        setError(describeLoginError(err));
      } finally {
        setIsLoading(false);
      }
    };

    void doLogin();
  };

  const handleSsoLogin = () => {
    setError("");
    setIsSsoLoading(true);

    const doSso = async () => {
      try {
        const config = await authApi.ssoConfig();
        const codeVerifier = await generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const state = generateState();

        // Store verifier and state in sessionStorage — survives the redirect but not across sessions
        sessionStorage.setItem("sso_code_verifier", codeVerifier);
        sessionStorage.setItem("sso_state", state);

        const params = new URLSearchParams({
          client_id: config.clientId,
          response_type: "code",
          redirect_uri: config.redirectUri,
          scope: "openid profile email User.Read",
          code_challenge: codeChallenge,
          code_challenge_method: "S256",
          state,
        });

        window.location.href = `https://login.microsoftonline.com/${config.tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
      } catch {
        setError("Unable to start Microsoft sign-in. Please try again.");
        setIsSsoLoading(false);
      }
    };

    void doSso();
  };

  const statsRef = useRef<HTMLDivElement>(null);
  const orbRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const isHoveringRef = useRef(false);

  // Lerp animation loop — GPU-accelerated via translate3d
  useEffect(() => {
    let running = true;
    const animate = () => {
      if (!running || !orbRef.current) return;
      const lerp = isHoveringRef.current ? 0.08 : 0.04;
      currentRef.current.x += (targetRef.current.x - currentRef.current.x) * lerp;
      currentRef.current.y += (targetRef.current.y - currentRef.current.y) * lerp;
      orbRef.current.style.transform = `translate3d(${String(currentRef.current.x)}px, ${String(currentRef.current.y)}px, 0)`;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { running = false; cancelAnimationFrame(rafRef.current); };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 240;
    const y = e.clientY - rect.top + e.currentTarget.scrollTop - 240;
    targetRef.current = { x, y };
    isHoveringRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Snap target back to hero center (50% of container width - half orb)
    if (rightPanelRef.current) {
      const w = rightPanelRef.current.clientWidth;
      targetRef.current = { x: w / 2 - 240, y: -128 };
    }
    isHoveringRef.current = false;
  }, []);

  // Mobile: observe when login section scrolls out of view to show floating sign-in
  useEffect(() => {
    if (!loginSectionRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry) setShowSignInFloat(!entry.isIntersecting); },
      { threshold: 0.15 }
    );
    obs.observe(loginSectionRef.current);
    return () => { obs.disconnect(); };
  }, []);

  // Mobile: track scroll position for red glow orb via ref on root container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const isMobileView = window.innerWidth < 768;
    if (!isMobileView) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handler = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 50;
      setMobileGlowY(pct);
    };
    container.addEventListener('scroll', handler, { passive: true });
    return () => { container.removeEventListener('scroll', handler); };
  }, []);

  const scrollToLanding = () => {
    landingSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToLogin = () => {
    loginSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const card = "bg-white/[0.03] border border-white/[0.06] rounded-xl";

  // Dev-only email and password form (isLocalLoginEnabled), shared by both layouts.
  const localLoginForm = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm text-white/70">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          required
          className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm text-white/70">
          Password
        </Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          required
          className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30"
        />
      </div>
      <Button
        type="submit"
        className="w-full bg-[#E11837] hover:bg-[#c01530] text-white"
        disabled={isLoading}
      >
        {isLoading ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );

  // Inside SpecTree Toolbox: the Toolbox's own sign-in (UX-PM-36, frame 1a), not the
  // marketing page. Same Entra handler, so the post-login route is unchanged. Placed
  // after every hook so the hook order is the same in both layouts. With local login
  // on (local development only) the password form shows below the Microsoft button,
  // as on the browser page; production never shows it.
  if (toolboxBridge) {
    return (
      <ToolboxSignIn
        version={toolboxBridge.app.version}
        onSignIn={handleSsoLogin}
        isSigningIn={isSsoLoading}
        error={error || undefined}
        {...(isLocalLoginEnabled ? { localLogin: localLoginForm } : {})}
      />
    );
  }

  return (
    <div ref={scrollContainerRef} className="flex flex-col md:flex-row min-h-screen bg-[#0a0a0b] overflow-y-auto md:overflow-hidden">
      {/* Mobile: red glow orb that follows scroll — behind all content */}
      <div
        className="fixed inset-0 pointer-events-none z-0 md:hidden"
        aria-hidden="true"
      >
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #E11837 0%, #FF4D6A 30%, transparent 70%)",
            top: `${String(mobileGlowY)}%`,
            transition: "top 0.3s ease-out",
            willChange: "top",
            filter: "blur(140px)",
          }}
        />
      </div>

      {/* ============================================================ */}
      {/*  LEFT — Sign-in form (fixed on desktop, in-flow on mobile)   */}
      {/* ============================================================ */}
      <div
        ref={loginSectionRef}
        className="relative md:fixed md:inset-y-0 md:left-0 w-full md:w-[40%] flex flex-col min-h-screen md:border-r border-white/[0.06] bg-[#0a0a0b] z-10 md:overflow-y-auto"
      >
        <div className="flex items-center gap-2.5 px-6 pt-6">
          <img src="/toro-logo.svg" alt="SpecTree" className="h-9" />
          <span className="text-white text-lg font-semibold tracking-tight">
            SpecTree
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-white">
                Welcome back
              </h1>
              <p className="text-sm text-white/50 mt-1">
                Sign in to your account
              </p>
            </div>

            {/* SSO error display — visible when handleSsoLogin sets error */}
            {error && (
              <div className="text-sm text-red-400 mb-4">{error}</div>
            )}

            {/* Dev-only manual login form — gated behind isLocalLoginEnabled */}
            {isLocalLoginEnabled && (
              <>
                {localLoginForm}

                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-white/[0.08]" />
                  <span className="text-[11px] text-white/25 uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-white/[0.08]" />
                </div>
              </>
            )}

            {/* Microsoft SSO Button */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
            >
              <Button
                type="button"
                className="w-full bg-[#E11837] hover:bg-[#c01530] text-white"
                disabled={isSsoLoading}
                onClick={handleSsoLogin}
              >
                {isSsoLoading ? (
                  <svg className="w-4 h-4 mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                )}
                {isSsoLoading ? "Redirecting…" : "Sign in with Microsoft"}
              </Button>
            </motion.div>

            {/* [HIDDEN] Invitation activation link — commented out, SSO-only flow
            <div className="mt-5 text-center text-sm text-white/40">
              Have an invitation code?{" "}
              <Link
                to="/activate"
                className="text-white/70 hover:text-white hover:underline"
              >
                Activate your account
              </Link>
            </div>
            */}

            {/* Legal text */}
            <p className="mt-6 text-[10px] text-white/20 leading-relaxed text-center">
              By signing in, you agree to the{" "}
              <a href="https://www.toro.com/en/legal/terms-of-use?srsltid=AfmBOooaOKc2n7qDcaokNpx_UY5Aw8U0gSuiHVfqtz_spN2nOJs_XP93" target="_blank" rel="noopener noreferrer" className="text-white/35 hover:text-white/50 underline">
                Terms of Use
              </a>
              .
            </p>

            {/* Mobile: Discover SpecTree — glowing red, inside the form area */}
            <button
              onClick={scrollToLanding}
              className="md:hidden relative mt-6 flex flex-col items-center gap-1.5 mx-auto group"
            >
              {/* Red glow behind */}
              <span className="absolute -inset-x-16 -inset-y-10 rounded-full bg-[#E11837]/25 blur-3xl animate-pulse pointer-events-none" />
              <span className="relative text-sm font-semibold tracking-wide text-[#E11837] group-hover:text-[#FF4D6A] transition-colors">
                Discover SpecTree
              </span>
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E11837"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="relative"
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <path d="m6 9 6 6 6-6" />
              </motion.svg>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 text-xs text-white/25 hidden md:block">
          © 2026 The Toro Company. All rights reserved.
        </div>

        {/* Mobile: copyright below form */}
        <div className="md:hidden flex justify-center px-6 pb-6">
          <p className="text-[10px] text-white/20">
            © 2026 The Toro Company. All rights reserved.
          </p>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  RIGHT — Product showcase (scrollable, Framer-style)         */}
      {/* ============================================================ */}
      <div
        ref={rightPanelRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="relative z-[1] w-full md:w-[60%] md:ml-[40%] md:overflow-y-auto md:min-h-screen"
        style={{ scrollbarWidth: "none" }}
      >
        <div ref={landingSectionRef} className="px-6 md:px-14 lg:px-20 py-16 md:py-20 space-y-20 md:space-y-28">
          {/* ---- Hero ---- */}
          <section className="relative">
            {/* Hue orb — follows cursor when hovering, snaps back to hero center on leave */}
            <div
              ref={orbRef}
              className="pointer-events-none absolute hidden md:block w-[480px] h-[480px] rounded-full opacity-20 blur-[120px]"
              style={{
                background:
                  "radial-gradient(circle, #E11837 0%, #FF6B6B 50%, transparent 70%)",
                left: 0,
                top: 0,
                willChange: "transform",
              }}
            />

            <FadeUp>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E11837]/60 mb-5">
                AI Project Management
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.08]">
                <span className="text-white">Build software with</span>
                <br />
                <span className="text-white">AI agents that </span>
                <span className="bg-gradient-to-r from-[#E11837] to-[#FF4D6A] bg-clip-text text-transparent">
                  actually ship
                </span>
              </h2>
              <p className="mt-6 text-[15px] text-white/40 max-w-lg leading-relaxed">
                SpecTree gives AI agents memory, structure, and accountability.
                Turn vague requests into planned, executed, and verified
                deliverables — across multiple agents working in parallel.
              </p>
            </FadeUp>
          </section>

          {/* ---- How it works ---- */}
          <section>
            <FadeUp>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25 mb-6">
                How it works
              </p>
            </FadeUp>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {steps.map((s, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <div
                    className={`${card} p-5 h-full hover:bg-white/[0.05] transition-colors duration-200`}
                  >
                    <s.Icon className="w-5 h-5 text-[#E11837]/70 mb-3" />
                    <p className="text-sm font-medium text-white">{s.title}</p>
                    <p className="mt-1 text-[11px] text-white/35 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </section>

          {/* ---- Plan (Visual UI Mockup) ---- */}
          <section>
            <FadeUp>
              <div className="flex items-center gap-2.5 mb-3">
                <TaskListSquareLtr24Regular className="w-5 h-5 text-[#E11837]/70" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E11837]/70">
                  Plan
                </p>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
                From vague idea to
                <br />
                structured execution
              </h3>
              <p className="mt-3 text-sm text-white/35 max-w-md leading-relaxed">
                Describe what you want in plain language. SpecTree decomposes it
                into epics with features, tasks, acceptance criteria, and
                complexity estimates.
              </p>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                {/* Epic header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/[0.06]">
                  <div className="w-2 h-2 rounded-full bg-[#E11837]" />
                  <span className="text-[13px] text-white/80 font-medium">
                    User Activity Dashboard
                  </span>
                  <span className="text-[10px] text-white/20 ml-auto">
                    3 features · 12 tasks
                  </span>
                </div>

                {/* Feature cards */}
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {[
                      { name: "Database Schema", complexity: "moderate", tasks: 3, progress: 67, status: "active" },
                      { name: "API Endpoints", complexity: "moderate", tasks: 4, progress: 25, status: "active" },
                      { name: "Dashboard UI", complexity: "complex", tasks: 5, progress: 0, status: "pending" },
                    ].map((f, i) => (
                      <div
                        key={i}
                        className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-white/[0.04] transition-colors"
                      >
                        <p className="text-[11px] text-white/60 font-medium truncate">
                          {f.name}
                        </p>
                        <div className="mt-2 flex items-center gap-1.5">
                          <div className="flex-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                f.progress > 50
                                  ? "bg-emerald-400/60"
                                  : f.progress > 0
                                  ? "bg-amber-400/60"
                                  : ""
                              }`}
                              style={{ width: `${String(f.progress)}%` }}
                            />
                          </div>
                          <span className="text-[9px] text-white/20 w-7 text-right">
                            {f.progress}%
                          </span>
                        </div>
                        <p className="mt-1.5 text-[9px] text-white/20">
                          {f.complexity} · {f.tasks} tasks
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Task list */}
                  <div className="mt-3 space-y-1">
                    {[
                      { text: "Create ActivityLog model", status: "done" },
                      { text: "Add indexes & relations", status: "progress" },
                      { text: "Generate Prisma client", status: "pending" },
                    ].map((t, i) => (
                      <div key={i} className="flex items-center gap-2.5 px-1 py-0.5">
                        {t.status === "done" ? (
                          <CheckmarkCircle16Filled className="w-4 h-4 text-emerald-400/70 shrink-0" />
                        ) : t.status === "progress" ? (
                          <div className="w-4 h-4 rounded-full border-2 border-amber-400/40 flex items-center justify-center shrink-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400/70 animate-pulse" />
                          </div>
                        ) : (
                          <Circle16Regular className="w-4 h-4 text-white/15 shrink-0" />
                        )}
                        <span
                          className={`text-[11px] flex-1 ${
                            t.status === "pending" ? "text-white/25" : "text-white/50"
                          }`}
                        >
                          {t.text}
                        </span>
                        <span
                          className={`text-[9px] ${
                            t.status === "done"
                              ? "text-emerald-400/50"
                              : t.status === "progress"
                              ? "text-amber-400/50"
                              : "text-white/15"
                          }`}
                        >
                          {t.status === "done"
                            ? "done"
                            : t.status === "progress"
                            ? "in progress"
                            : "pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>
          </section>

          {/* ---- Execute (Visual UI Mockup) ---- */}
          <section>
            <FadeUp>
              <div className="flex items-center gap-2.5 mb-3">
                <Rocket24Regular className="w-5 h-5 text-[#E11837]/70" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E11837]/70">
                  Execute
                </p>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
                Parallel agents,
                <br />
                coordinated phases
              </h3>
              <p className="mt-3 text-sm text-white/35 max-w-md leading-relaxed">
                SpecTree orchestrates multiple AI agents working simultaneously.
                Dependencies are respected, context is shared, and phases
                advance automatically.
              </p>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/[0.06]">
                  <Play20Filled className="w-4 h-4 text-[#E11837]/70" />
                  <span className="text-[13px] text-white/80 font-medium">
                    Execution Progress
                  </span>
                  <span className="text-[10px] text-amber-400/60 ml-auto">
                    Phase 2 of 3
                  </span>
                </div>

                {/* Phase 1 - Complete */}
                <div className="px-5 py-3.5 border-b border-white/[0.04]">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[11px] text-white/40 font-medium">Phase 1</span>
                    <span className="text-[10px] text-emerald-400/60 flex items-center gap-1">
                      <CheckmarkCircle16Filled className="w-3 h-3" />
                      Complete
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg border border-emerald-400/15 bg-emerald-400/[0.04] p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-emerald-400/15 flex items-center justify-center">
                          <CheckmarkCircle16Filled className="w-3.5 h-3.5 text-emerald-400/80" />
                        </div>
                        <div>
                          <p className="text-[11px] text-white/60 font-medium">Database Schema</p>
                          <p className="text-[9px] text-white/20">agent-1 · 4m 23s</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phase 2 - In Progress */}
                <div className="px-5 py-3.5 border-b border-white/[0.04]">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[11px] text-white/40 font-medium">Phase 2</span>
                    <span className="text-[10px] text-amber-400/60">In Progress</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg border border-emerald-400/15 bg-emerald-400/[0.04] p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-emerald-400/15 flex items-center justify-center">
                          <CheckmarkCircle16Filled className="w-3.5 h-3.5 text-emerald-400/80" />
                        </div>
                        <div>
                          <p className="text-[11px] text-white/60 font-medium">API Endpoints</p>
                          <p className="text-[9px] text-white/20">agent-2 · 8m 12s</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 rounded-lg border border-amber-400/15 bg-amber-400/[0.03] p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-amber-400/15 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-amber-400/80 animate-pulse" />
                        </div>
                        <div>
                          <p className="text-[11px] text-white/60 font-medium">Dashboard UI</p>
                          <p className="text-[9px] text-amber-400/40">in progress…</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-center">
                    <span className="text-[9px] text-white/15 bg-white/[0.03] px-2.5 py-0.5 rounded-full">
                      ↕ running in parallel
                    </span>
                  </div>
                </div>

                {/* Phase 3 - Pending */}
                <div className="px-5 py-3.5">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[11px] text-white/25 font-medium">Phase 3</span>
                    <span className="text-[10px] text-white/15">Pending</span>
                  </div>
                  <div className="flex gap-2 opacity-40">
                    <div className="flex-1 rounded-lg border border-white/[0.04] bg-white/[0.01] p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-white/[0.04] flex items-center justify-center">
                          <Circle16Regular className="w-3.5 h-3.5 text-white/20" />
                        </div>
                        <div>
                          <p className="text-[11px] text-white/40 font-medium">E2E Testing</p>
                          <p className="text-[9px] text-white/15">waiting on Phase 2</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          </section>

          {/* ---- Monitor (Enhanced Terminal) ---- */}
          <section>
            <FadeUp>
              <div className="flex items-center gap-2.5 mb-3">
                <Eye24Regular className="w-5 h-5 text-[#E11837]/70" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E11837]/70">
                  Monitor
                </p>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
                Real-time visibility into
                <br />
                every decision
              </h3>
              <p className="mt-3 text-sm text-white/35 max-w-md leading-relaxed">
                Watch AI agents work live. Every event, decision, and progress
                update streams in real time — full transparency into
                what’s happening and why.
              </p>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="mt-6">
                <MockTerminal title="session monitor">
                  <div className="space-y-0.5">
                    {[
                      { time: "14:32:01", icon: "●", color: "text-blue-400/60", type: "feature_started", detail: "Database Schema" },
                      { time: "14:33:22", icon: "✓", color: "text-emerald-400/60", type: "task_completed", detail: "Create ActivityLog model" },
                      { time: "14:33:45", icon: "◆", color: "text-purple-400/60", type: "decision_made", detail: "Use composite index for queries" },
                      { time: "14:35:12", icon: "✓", color: "text-emerald-400/60", type: "validation_passed", detail: "TypeScript compiles (0 errors)" },
                      { time: "14:38:01", icon: "●", color: "text-blue-400/60", type: "task_started", detail: "Add indexes & relations" },
                      { time: "14:42:33", icon: "△", color: "text-amber-400/60", type: "progress_note", detail: "Adjusting schema for edge case" },
                      { time: "14:44:18", icon: "✓", color: "text-emerald-400/60", type: "task_completed", detail: "Add indexes & relations" },
                    ].map((e, i) => (
                      <div key={i} className="flex gap-3">
                        <span className="text-white/20 w-16 shrink-0">{e.time}</span>
                        <span className={`${e.color} w-4 text-center`}>{e.icon}</span>
                        <span className="text-white/30 w-28 shrink-0 truncate">{e.type}</span>
                        <span className="text-white/55">{e.detail}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-white/20 text-[10.5px]">
                    7 events · 2 decisions · 0 blocked
                  </div>
                </MockTerminal>
              </div>
            </FadeUp>
          </section>

          {/* ---- Validate (Visual UI Mockup) ---- */}
          <section>
            <FadeUp>
              <div className="flex items-center gap-2.5 mb-3">
                <ShieldCheckmark20Filled className="w-5 h-5 text-[#E11837]/70" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E11837]/70">
                  Validate
                </p>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
                Done means
                <br />
                actually done
              </h3>
              <p className="mt-3 text-sm text-white/35 max-w-md leading-relaxed">
                Every task has acceptance criteria verified by automated checks.
                No more “it looks right to me” — real proof that the
                work is complete.
              </p>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/[0.06]">
                  <ShieldCheckmark20Filled className="w-4 h-4 text-emerald-400/70" />
                  <span className="text-[13px] text-white/80 font-medium">
                    Validation Report
                  </span>
                  <span className="text-[10px] text-emerald-400/60 ml-auto flex items-center gap-1">
                    <CheckmarkCircle16Filled className="w-3 h-3" />
                    4/5 passed
                  </span>
                </div>

                {/* Check items */}
                <div className="divide-y divide-white/[0.03]">
                  {[
                    { label: "TypeScript compiles", cmd: "pnpm exec tsc --noEmit", result: "exit 0", time: "0.8s", pass: true },
                    { label: "Schema exists", cmd: "file_exists schema.prisma", result: "found", time: "0.1s", pass: true },
                    { label: "Route registered", cmd: "file_contains router.get.*activity", result: "matched", time: "0.2s", pass: true },
                    { label: "Tests pass", cmd: "pnpm test data-layer.test.ts", result: "12 passed", time: "3.2s", pass: true },
                    { label: "Dark mode rendering", cmd: "manual review", result: "pending", time: "—", pass: false },
                  ].map((c, i) => (
                    <div key={i} className="flex items-center gap-3 px-5 py-3">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                          c.pass ? "bg-emerald-400/15" : "bg-amber-400/10"
                        }`}
                      >
                        {c.pass ? (
                          <CheckmarkCircle16Filled className="w-3.5 h-3.5 text-emerald-400/80" />
                        ) : (
                          <Circle16Regular className="w-3.5 h-3.5 text-amber-400/60" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-white/60 font-medium">{c.label}</p>
                        <p className="text-[9px] text-white/20 font-mono truncate">{c.cmd}</p>
                      </div>
                      <span className={`text-[10px] shrink-0 ${c.pass ? "text-emerald-400/50" : "text-amber-400/40"}`}>
                        {c.result}
                      </span>
                      <span className="text-[9px] text-white/15 w-8 text-right shrink-0">
                        {c.time}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div className="px-5 py-3 border-t border-white/[0.06] flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full w-[80%] rounded-full bg-emerald-400/60" />
                  </div>
                  <span className="text-[11px] text-emerald-400/70 font-medium">80%</span>
                </div>
              </div>
            </FadeUp>
          </section>

          {/* ---- Capabilities Grid ---- */}
          <section>
            <FadeUp>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25 mb-3">
                Capabilities
              </p>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
                Everything you need to
                <br />
                scale AI development
              </h3>
            </FadeUp>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {capabilities.map((c, i) => (
                <FadeUp key={i} delay={i * 0.04}>
                  <div
                    className={`${card} p-5 h-full hover:bg-white/[0.05] hover:border-white/[0.1] transition-all duration-200 group`}
                  >
                    <c.Icon className="w-5 h-5 text-[#E11837]/50 group-hover:text-[#E11837]/70 transition-colors mb-3" />
                    <p className="text-[13px] font-medium text-white">
                      {c.title}
                    </p>
                    <p className="mt-1 text-[11.5px] text-white/35 leading-relaxed">
                      {c.desc}
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </section>

          {/* ---- Works with ---- */}
          <section>
            <FadeUp>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25 mb-6">
                Works with your tools
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {integrations.map((t, i) => (
                  <div
                    key={i}
                    className={`${card} p-4 text-center hover:bg-white/[0.05] hover:border-white/[0.1] transition-all duration-200 flex flex-col items-center`}
                  >
                    <span className="text-white/30">
                      {t.icon}
                    </span>
                    <p className="mt-1.5 text-[11px] text-white/40">
                      {t.name}
                    </p>
                  </div>
                ))}
              </div>
            </FadeUp>
          </section>

          {/* ---- Stats ---- */}
          <section>
            <FadeUp>
              <div
                ref={statsRef}
                className={`${card} grid grid-cols-3 divide-x divide-white/[0.06] py-10`}
              >
                <div className="text-center">
                  <p className="text-3xl font-bold text-white tracking-tight">
                    <Counter target={95} suffix="+" containerRef={statsRef} />
                  </p>
                  <p className="mt-1 text-[11px] text-white/25">MCP tools</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white tracking-tight">
                    <Counter target={120} suffix="+" containerRef={statsRef} />
                  </p>
                  <p className="mt-1 text-[11px] text-white/25">API endpoints</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white tracking-tight">
                    <Counter target={5} containerRef={statsRef} />
                  </p>
                  <p className="mt-1 text-[11px] text-white/25">Stage pipeline</p>
                </div>
              </div>
            </FadeUp>
          </section>

          {/* ---- Quote ---- */}
          <section className="pb-16">
            <FadeUp>
              <blockquote className="text-lg md:text-xl text-white/30 leading-relaxed w-full">
                “SpecTree doesn’t replace your engineers. It makes
                every AI-assisted project manageable, predictable, and
                visible.”
              </blockquote>
              <p className="mt-4 text-[11px] text-white/15">
                Built for teams shipping with AI
              </p>
            </FadeUp>
          </section>
        </div>
      </div>

      {/* Mobile: floating Sign In button when scrolled past login */}
      {showSignInFloat && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="md:hidden fixed bottom-4 left-4 right-4 z-50"
        >
          <button
            onClick={scrollToLogin}
            className="w-full py-3.5 rounded-2xl bg-[#E11837] text-white font-semibold text-sm shadow-2xl shadow-[#E11837]/30 active:scale-[0.97] transition-transform"
          >
            Sign In
          </button>
        </motion.div>
      )}
    </div>
  );
}
